import { Show, createEffect, onCleanup } from "solid-js";

function findDynamicNodes(svgEl: SVGSVGElement) {
  const percentTspans = Array.from(svgEl.querySelectorAll("tspan")).filter(
    (t) => (t.textContent || "").trim().startsWith("%")
  );
  const percentText = percentTspans[0]?.parentNode as SVGTextElement | null;
  if (!percentText)
    return { digitText: null, percentText: null, trailingTexts: [] };

  const percentTspan = percentText.querySelector("tspan");
  const yRef = parseFloat(percentTspan?.getAttribute("y") || "0");
  const percentX = parseFloat(percentTspan?.getAttribute("x") || "0");

  const candidates = Array.from(svgEl.querySelectorAll("text"))
    .filter((t) => t !== percentText)
    .map((t) => {
      const tspan = t.querySelector("tspan");
      if (!tspan) return null;
      const text = (tspan.textContent || "").trim();
      if (!/^(\d+(\.\d+)?|X)$/i.test(text)) return null;
      return {
        node: t as SVGTextElement,
        dy: Math.abs(parseFloat(tspan.getAttribute("y") || "0") - yRef),
      };
    })
    .filter(Boolean)
    .sort((a, b) => a!.dy - b!.dy);

  const digitText = candidates[0]?.node ?? null;

  const trailingTexts = Array.from(svgEl.querySelectorAll("text")).filter(
    (t) => {
      if (t === percentText || t === digitText) return false;
      const tspan = t.querySelector("tspan");
      if (!tspan) return false;
      const textY = parseFloat(tspan.getAttribute("y") || "0");
      const textX = parseFloat(tspan.getAttribute("x") || "0");
      return Math.abs(textY - yRef) < 5 && textX > percentX;
    }
  ) as SVGTextElement[];

  return { digitText, percentText, trailingTexts };
}

function injectValue(rawSvg: string, value: number): string | null {
  if (typeof DOMParser === "undefined") return rawSvg;

  const parser = new DOMParser();
  const doc = parser.parseFromString(rawSvg, "image/svg+xml");
  const svgEl = doc.querySelector("svg");
  if (!svgEl) return null;

  const { digitText, percentText } = findDynamicNodes(svgEl);
  if (digitText) {
    const tspan = digitText.querySelector("tspan");
    if (tspan) {
      tspan.textContent = ` ${value}`;
    }
  }

  const titleBaselineY = percentText
    ? parseFloat(percentText.querySelector("tspan")?.getAttribute("y") || "0")
    : null;

  const textElements = svgEl.querySelectorAll("text");
  textElements.forEach((textEl) => {
    const currentFont = textEl.getAttribute("font-family");
    const textY = parseFloat(
      textEl.querySelector("tspan")?.getAttribute("y") || "0"
    );
    const isTitle =
      titleBaselineY !== null && Math.abs(textY - titleBaselineY) < 5;

    if (currentFont?.includes("Inter Display")) {
      textEl.setAttribute(
        "font-family",
        "Inter, system-ui, -apple-system, sans-serif"
      );
      if (isTitle) {
        textEl.setAttribute("font-weight", "600");
      }
    } else if (currentFont?.includes("Inter")) {
      textEl.setAttribute(
        "font-family",
        "Inter, system-ui, -apple-system, sans-serif"
      );
      if (isTitle) {
        textEl.setAttribute("font-weight", "600");
      }
    }
  });

  svgEl.setAttribute("width", "100%");
  svgEl.setAttribute("height", "100%");
  svgEl.setAttribute("preserveAspectRatio", "none");

  return new XMLSerializer().serializeToString(svgEl);
}

function repositionPercentSign(rootEl: HTMLElement, gap = 1) {
  const svgEl = rootEl.querySelector("svg") as SVGSVGElement | null;
  if (!svgEl) return;

  const { digitText, percentText, trailingTexts } = findDynamicNodes(svgEl);
  if (!digitText || !percentText) return;

  const digitTspan = digitText.querySelector("tspan");
  const percentTspan = percentText.querySelector("tspan");
  if (!digitTspan || !percentTspan) return;

  let bbox: DOMRect;
  try {
    bbox = digitTspan.getBBox();
  } catch {
    return;
  }

  const originalPercentX = parseFloat(percentTspan.getAttribute("x") || "0");
  const newPercentX = bbox.x + bbox.width + gap;
  const offsetX = newPercentX - originalPercentX;

  percentTspan.setAttribute("x", String(newPercentX));

  for (const trailingText of trailingTexts) {
    const tspan = trailingText.querySelector("tspan");
    if (tspan) {
      const currentX = parseFloat(tspan.getAttribute("x") || "0");
      tspan.setAttribute("x", String(currentX + offsetX));
    }
  }
}

interface DynamicSvgOverlayProps {
  rawSvg: string | undefined | null;
  value: number;
}

export function DynamicSvgOverlay(props: DynamicSvgOverlayProps) {
  let overlayRef: HTMLDivElement | undefined;

  const processedSvg = () => {
    if (!props.rawSvg) return null;
    return injectValue(props.rawSvg, props.value);
  };

  createEffect(() => {
    const markup = processedSvg();
    if (!markup || !overlayRef) return;

    if (typeof requestAnimationFrame === "undefined") return;

    const id = requestAnimationFrame(() => {
      if (overlayRef) {
        repositionPercentSign(overlayRef);
      }
    });

    onCleanup(() => cancelAnimationFrame(id));
  });

  return (
    <Show when={processedSvg()}>
      <div
        ref={overlayRef}
        class="pointer-events-none absolute left-0 top-0 h-full w-full"
        innerHTML={processedSvg()!}
      />
    </Show>
  );
}
